import { Flex } from 'antd';
import CardDetailButton from '../cardDetailButton/cardDetailButton';
import InteractiveCard from '../common/interactiveCard';
import CardData from '../dashboard/cardData';
import FavouriteStatus from '../favouriteStatus/favouriteStatus';
import { Divider } from '../Ant';
import { useTranslation } from 'react-i18next';
import { Briefcase, Calendar, Share, ShieldTick } from '../icons';
import { IMatter } from '../../interface/matter';
import { getTranslatedRoles } from '../../constants/conflictSearchUtility';
import { ICustomerType } from '../../constants/customer';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { IMatterParties } from '../../interface/matterParties';
import { ROUTES } from '../../constants/constants';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { MatterStatus } from '../../constants/matter';
import NoteIcons from '../common/NoteIcons';
import { BorderColor, TagColor } from '../../interface/common';
import { getLocalDateAndTime, getMatterIconClass } from '../../constants/utilityFunctions';
import { MatterIndicators } from '../matters/matterIndicators';
import { InvoiceableType } from '../../constants/transaction';

const MatterCard = ({ matter, type }: { matter: IMatter; type: string }) => {
  const { t } = useTranslation();
  const { push } = useNavigationStack();
  const { id: customerId } = useParams();

  const { getAppLanguageCode } = useLanguage();

  const handleMatterDetail = () => {
    push(`${ROUTES.MATTER_DETAIL}/${matter.id}`);
  };
  return (
    <InteractiveCard
      data-testid={`matter-card`}
      className={`pointer w-full customer-card ${matter?.status === MatterStatus.ACTIVE ? BorderColor.GREEN : BorderColor.RED}`}
      onActivate={handleMatterDetail}
    >
      <Flex justify="space-between" align="center" className="pb-10">
        <Flex gap={8} align="center">
          <Flex
            data-testid={`matter-status-chip`}
            justify="center"
            align="center"
            className={`status-chip ${matter?.status === MatterStatus.ACTIVE ? TagColor.GREEN : TagColor.RED}`}
          >
            {t('matter:status.' + matter.status)}
          </Flex>
          <NoteIcons
            hasImportantNotes={matter?.hasImportantNotes}
            hasImportantRelatedNotes={matter?.hasImportantRelatedNotes}
            hasNotes={matter?.hasNotes}
          />
          <MatterIndicators
            invoiceableType={matter?.invoiceableType as InvoiceableType}
            hasSubMatters={Boolean(matter?.hasSubMatters)}
            parentMatterId={matter?.parentMatterId}
            fixedPriceInUse={matter?.fixedPriceInUse}
            insider={matter?.insider}
            fixedPrice={matter?.fixedPrice}
            currency={matter?.currency}
          />
        </Flex>

        <Flex gap={10}>
          <FavouriteStatus
            isFavourite={matter?.isFavorite}
            testId="favourite-icon"
            tooltip={t('common:favorite')}
          />
          <CardDetailButton onOpen={handleMatterDetail} testId="matter-card-go-detail" />
        </Flex>
      </Flex>
      <Divider className="mb-10" />
      <Flex vertical gap={10}>
        <CardData
          type="matter"
          icon={<Briefcase className={getMatterIconClass(matter?.invoiceableType ?? '')} />}
          title={t('matter:matter')}
          content={
            matter?.principalCompany?.name
              ? matter?.principalCompany?.name
              : (matter?.principalPrivatePerson?.fullName ?? '')
          }
          subcontent={matter?.matterNumber + ' - ' + matter?.subject}
          contentclass="lineclamp-1"
          subcontentclass="lineclamp-1"
        />
        {matter.parties && (
          <CardData
            icon={<Share />}
            title={t('matter:roles')}
            type="roles"
            content={getTranslatedRoles(
              (
                matter?.parties?.find((party) =>
                  type === ICustomerType.COMPANY
                    ? party.companyId === customerId
                    : party.privatePersonId === customerId,
                ) as IMatterParties
              )?.roles ?? [],
              getAppLanguageCode(),
            )}
          />
        )}
        <Divider />
        <Flex justify="space-between">
          <CardData
            type="responsible-person"
            icon={<ShieldTick />}
            title={t('matter:responsiblePerson')}
            content={matter?.responsibleUser?.fullName ?? '-'}
          />
          <CardData
            type="opening-date"
            icon={<Calendar />}
            title={t('matter:openingDate')}
            content={getLocalDateAndTime(new Date(matter?.openingDate ?? ''), true)}
          />
        </Flex>
      </Flex>
    </InteractiveCard>
  );
};

export default MatterCard;
